import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { RmsModule } from '@rms';
import { CustomerCrmService, RmsMessageService, RmsPulldownService } from '@services';
import { PaymentPreferenceFormComponent } from './payment-preference-form/payment-preference-form.component';

@Component({
  selector: 'app-customer-payment-preferences',
  imports: [RmsModule],
  templateUrl: './customer-payment-preferences.component.html',
  styleUrl: './customer-payment-preferences.component.scss'
})
export class CustomerPaymentPreferencesComponent implements OnChanges, OnDestroy {
  @Input() companyId!: number;
  @Input() canEdit = false;
  Preference: any = {};
  PaymentMethods: any[] = [];
  PaymentTerms: any[] = [];
  private saveTimeout: any;

  // Net Cash vs. Docs (1) and Net Cash upon release (2) are cash-based terms -
  // no credit is being extended, so Credit Limit doesn't apply.
  private readonly CASH_TERM_IDS = [1, 2];

  get isCashTerm(): boolean {
    return this.CASH_TERM_IDS.includes(this.Preference.PaymentTermId);
  }

  constructor(
    private crm: CustomerCrmService,
    private message: RmsMessageService,
    private pulldown: RmsPulldownService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['companyId']) {
      clearTimeout(this.saveTimeout);
      this.Preference = {};
      this.loadLookups();
      this.load();
    }
  }

  ngOnDestroy() { clearTimeout(this.saveTimeout); }

  loadLookups() {
    this.crm.getPaymentMethods().subscribe({
      next: (methods) => this.PaymentMethods = methods || [],
      error: (err) => this.message.error('Failed to load payment methods: ' + this.errorText(err))
    });
    this.crm.getPaymentTerms().subscribe({
      next: (terms) => this.PaymentTerms = terms || [],
      error: (err) => this.message.error('Failed to load payment terms: ' + this.errorText(err))
    });
  }

  load() {
    if (!this.companyId) return;
    const companyId = this.companyId;
    this.crm.getPaymentPreference(companyId).subscribe({
      next: (preference) => { if (companyId === this.companyId) this.Preference = preference || {}; },
      error: (err) => this.message.error('Failed to load payment preference: ' + this.errorText(err))
    });
  }

  // Preferred Method and Payment Terms are edited inline on the card and save
  // immediately - merge_customer_payment_preference always writes all three
  // columns together, so every save (inline or via the modal) must resend the
  // full current Preference, not just the one field that changed.
  savePreference() {
    if (!this.canEdit) return;
    clearTimeout(this.saveTimeout);
    if (this.isCashTerm) {
      this.Preference.CreditLimit = null;
    }
    const companyId = this.companyId;
    const payload = {
        CompanyID: companyId,
        PaymentMethodID: this.Preference.PaymentMethodID,
        PaymentTermId: this.Preference.PaymentTermId,
        CreditLimit: this.Preference.CreditLimit,
        ACHAuthorizationRecipientEmail: this.Preference.ACHAuthorizationRecipientEmail,
        ModifiedBy: localStorage.getItem('GUID')
      };
    this.saveTimeout = setTimeout(() => {
      if (companyId !== this.companyId) return;
      this.crm.savePaymentPreference(payload).subscribe({
        next: () => {
          this.message.success('Payment preference saved');
          if (companyId === this.companyId) this.load();
        },
        error: (err) => this.message.error('Failed to save payment preference: ' + this.errorText(err))
      });
    }, 600);
  }

  openEditForm() {
    if (!this.canEdit) return;
    const ref = this.pulldown.open(PaymentPreferenceFormComponent, {
      title: 'Edit ACH Authorization Recipient',
      width: 'clamp(400px, 40vw, 600px)',
      data: { preference: this.Preference },
      top: "0"
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      this.Preference.ACHAuthorizationRecipientEmail = result.ACHAuthorizationRecipientEmail;
      this.savePreference();
    });
  }

  private errorText(err: any): string {
    return err?.error?.error?.message || err?.message || 'Unknown error';
  }
}
